import React from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  ListItemButton,
  Typography,
  Avatar, 
  Menu, 
  MenuItem, 
  IconButton, 
  Tooltip,
  styled 
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccountBalance,
  Leaderboard,
  SmartToy,
  Settings as SettingsIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useAuth } from "../util/auth";
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import UserProfileModal from './UserProfileModal';
import BonusWatcherLogo from './BonusWatcherLogo';

const Sidebar = styled(Box)(({ theme }) => ({
  width: 187,
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  height: '100vh',
  position: 'sticky',
  top: 0,
  color: theme.palette.text.primary,
  transition: 'background-color 0.2s ease'
}));

const Logo = styled(Box)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 'bold',
  marginBottom: 40,
  padding: 20,
  color: theme.palette.text.primary
}));

const NavItem = styled(ListItemButton)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '15px 20px',
  color: theme.palette.text.primary,
  textDecoration: 'none',
  transition: 'all 0.3s ease',
  borderRadius: 8,
  marginBottom: 8,
  backgroundColor: active ? theme.palette.action.selected : 'transparent',

  '&:hover': {
    backgroundColor: theme.palette.action.hover
  },

  '& svg': {
    marginRight: 10,
    color: theme.palette.text.secondary
  }
}));

const UserSection = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  padding: 20,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const UserInfo = styled(Box)(({ theme }) => ({
  flex: 1
}));

const UserName = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: 14,
  color: theme.palette.text.primary
}));

const UserEmail = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.text.secondary
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover
  }
}));

const StyledLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'isActive'
})(({ theme, isActive }) => ({
  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isActive ? 
    theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)'
    : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(99, 102, 241, 0.15)' 
      : 'rgba(99, 102, 241, 0.08)',
  }
}));

const menuItems = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    icon: <DashboardIcon />
  },
  {
    title: 'Bonus Watcher',
    path: '/bonus-watcher',
    icon: <MonetizationOnIcon />
  },
  {
    title: 'Leaderboard',
    path: '/leaderboard',
    icon: <LeaderboardIcon />
  },
  {
    title: 'Friends',
    path: '/friends',
    icon: <PeopleIcon />
  }
];

function DashboardSidebar() {
  const location = useLocation();
  const auth = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isProfileOpen, setIsProfileOpen] = React.useState(false);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    auth.signout();
    handleMenuClose();
  };

  const handleProfileClick = () => {
    setIsProfileOpen(true);
    handleMenuClose();
  };

  return (
    <>
      <Sidebar>
        <Box sx={{ px: 2.5, py: 3, display: 'flex', justifyContent: 'center' }}>
          <BonusWatcherLogo />
        </Box>
        <nav>
          {menuItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.title}
                  secondary={item.title === 'Profile' ? auth.user?.email : null}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    color: 'text.secondary',
                    noWrap: true,
                    sx: { mt: 0 }
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </nav>

        <UserSection onClick={handleProfileClick}>
          <Avatar 
            sx={{ 
              bgcolor: 'primary.main',
              width: 40,
              height: 40,
            }}
          >
            {auth.user?.email?.[0]?.toUpperCase()}
          </Avatar>
          <UserInfo>
            <UserName>{auth.user?.email?.split('@')[0]}</UserName>
            <UserEmail>{auth.user?.email}</UserEmail>
          </UserInfo>
          <Tooltip title="Account settings">
            <ActionButton
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e);
              }}
              size="small"
            >
              <SettingsIcon />
            </ActionButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: -5,
                backgroundColor: '#1a1a1a',
                color: 'white',
                '& .MuiMenuItem-root': {
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                },
              },
            }}
          >
            <MenuItem onClick={handleProfileClick}>
              <AccountCircleIcon sx={{ mr: 1 }} /> Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </UserSection>
      </Sidebar>

      <UserProfileModal 
        open={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />
    </>
  );
}

export default DashboardSidebar; 